/**
 * The main JavaScript file for the AI4SoilHealthClient application.
 * It imports necessary dependencies, sets up configurations, and initializes the Vue app.
 * It also defines utility functions for handling Axios responses and errors,
 * as well as a function for logging out the user.
 * @module main
 */
import { createApp } from 'vue'
import App from './App.vue'
import axios from 'axios'
import i18n from './i18n.js'
import {
  Quasar,
  LocalStorage,
  SessionStorage,
  Dialog,
  Notify
} from 'quasar'

import '@quasar/extras/material-icons/material-icons.css'

import 'quasar/src/css/index.sass'
import './style.css'
import '../specific/app.scss'
import { store } from "./store.js"
import { GlobalMixin } from "./mixins/global.js"
import { GlobalApiMixin } from "./mixins/global-api.js"
import router from '../router.js'
import Keycloak from 'keycloak-js'; 

import { icons } from '../specific/additional-imports.js'
// common extra icons
import { symOutlinedTextToSpeech } from '@quasar/extras/material-symbols-outlined'
icons.text_to_speech = symOutlinedTextToSpeech;

import { loadComponent } from './component-loader.js'
let Header = loadComponent('header');
let HelpButton = loadComponent('help-button');
let CustomDialog = loadComponent('custom-dialog');

// testing in local network:
// .env:
// VITE_ROOT_API=http://localIP:port/api
// launchsettings.json:
// "applicationUrl": "http://localhost:port"
// applicationhost.config:
// <binding protocol="http" bindingInformation="*:port:*" />
// run:
// iisexpress-proxy port to port


/**
 * Logs out the user by removing the token from local storage and clearing user data.
 * If Keycloak is available, it also performs a Keycloak logout.
 */
function logout() {
  if (isLoggingOut) return;
  isLoggingOut = true;
  store.userData = null;
  app.config.globalProperties.$q.localStorage.remove("token");
  app.config.globalProperties.$q.localStorage.remove('userData');
  if (app.config.globalProperties.$keycloak) {
    app.config.globalProperties.$keycloak.logout({ redirectUri: import.meta.env.VITE_APP_URL });
    app.config.globalProperties.$keycloak.token = null;
  }
}

/**
 * Handles the response from an Axios request.
 * @param {object} response - The response object from Axios.
 * @returns {object} - The modified response object.
 */
async function handleAxiosResponse(response) {
  store.tenantId = 0;
  store.working = false;
  if (response.data) {
    if (response.data.error) {
          await app.config.globalProperties.$q.dialog({component: CustomDialog,
            componentProps: {
              error: true, title: i18n.global.t("Error"),
              message: response.data.error, type: 'Ok'
            }
          });
          return { data: null };
      } else if (response.data.message) {
          await app.config.globalProperties.$q.dialog({component: CustomDialog,
            componentProps: {
              error: true, title: i18n.global.t("Message"),
              message: response.data.message, type: 'Ok',
              persistent: true
            }
          });
      }
  }
  return response;
}

async function handleAxiosError(error) {
  store.tenantId = 0;
  store.working = false;
  let reason = "";
  let expired = false;
  console.log("Error", error);
  if (error.response) {
    let response = error.response;
    if (response.data instanceof Blob) {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);

        reader.readAsText(response.data);
      });

      try {
        const data = JSON.parse(text);

        reason =
          data.error ||
          data.message ||
          JSON.stringify(data);
      } catch {
        reason = text;
      }
    }  else if (response.status == 401) {
      // extract www-authenticate header from response
      //let header = response.headers.get("WWW-Authenticate");
      let header = response.headers["www-authenticate"] ?? response.headers["WWW-Authenticate"];
      if (header && header.indexOf("expired") > 0) {
        reason = i18n.global.t("Session expired - please login again");
        expired = true;
      } else {
        console.log(response);
        reason =  i18n.global.t("Unauthorized") + ' ' + (response?.data?.reason ?? '');
        if (store.userData && !app.config.globalProperties.$keycloak?.token) {
          store.userData = null;
        }
        //expired = true;
      }
    } else if (response.status == 429) {
      reason = i18n.global.t("Too many requests in a short time. Please try again a bit later.");
    } else {
      if (typeof response.data === "string") {
        reason = response.data;
      } else {
        reason =
          response.data?.message ||
          response.data?.error ||
          response.data?.title ||
          error.message;

        if (response.data?.errors) {
          reason += '<br>' +
            Object.values(response.data.errors)
              .flat()
              .join("<br>");
        }
      }
    }
  } else if (error.request) {
    reason = i18n.global.t("Error request") + ": " + error;  //i18n.global.t("No response from server");
    //logout();
    return { data: null };
    // expired = true;
  } else {
    reason = i18n.global.t("Error else") + ": " + error;
  }

  if (reason > "") {
    await app.config.globalProperties.$q.dialog({
      component: CustomDialog,
      componentProps: {
        error: true, title: i18n.global.t("Error"), message: reason, type: 'Ok'
      }
    });
  }

  if (expired) {
    logout();
  }

  return { data: null };
}    
        
console.dir(import.meta.env);
store.baseURL = import.meta.env.BASE_URL || ""; // for dynamic logo loading in public folder

let errors = [];
window.onerror = function (messageOrEvent, source, lineno, colno, error) {
  let s = "Error:" + messageOrEvent + "\n" + 
    "Source:" + source + " Line:" + lineno + " Col:" + colno;
  errors.push(s);
  return true;
}

/**
 * Axios instance for making HTTP requests.
 */
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_ROOT_API,
});

/**
 * Axios instance for making generic API requests.
 */
const axiosInstanceGeneric = axios.create({
  baseURL: "" //import.meta.env.VITE_STAC_API
});

axiosInstance.interceptors.response.use(
  (response) => handleAxiosResponse(response),
  (error) => handleAxiosError(error)
);

axiosInstance.interceptors.request.use(
  (config) => {
    if (app.config.globalProperties.$keycloak && app.config.globalProperties.$keycloak.token) {
      config.headers['Authorization'] = 'Bearer ' + app.config.globalProperties.$keycloak.token;
    }
    config.headers['LangId'] = store.langId;
    config.headers['TenantId'] = store.tenantId;
    if (store.EU) config.headers['EU'] = store.EU.value;
    return config;
  },
  (error) => handleAxiosError(error)
);

axiosInstanceGeneric.interceptors.response.use(
  (response) => handleAxiosResponse(response),
  (error) => handleAxiosError(error)
);
    
const app = createApp(App);

app.config.globalProperties.axios = {
  API: axiosInstance,
  APIGen: axiosInstanceGeneric
};


let authenticated = false;

app.config.errorHandler = function (err, vm, info) {
  console.error(`Error: ${err.toString()}\nInfo: ${info}`)
  if (err.stack) {
    const stack = err.stack.split('\n')[1].trim()
    const [moduleName, lineNo, colNo] = stack.match(/at\s+(.+):(\d+):(\d+)/).slice(1)
    let s = "Error:" + err.message + "\n" + 
    "Source:" + moduleName + " Line:" + lineNo + " Col:" + colNo;
    console.error(`Module: ${moduleName}, Line: ${lineNo}, Column: ${colNo}`)
    errors.push(s);
  }
}
app.config.globalProperties.$icons = icons;
app.config.globalProperties.$logout = logout;
app.config.globalProperties.$errors = errors;
app.config.globalProperties.$store = store; 
app.config.globalProperties.$i18n = i18n;
import packageJson from '../../package.json';
app.config.globalProperties.$store.version = packageJson.version; //import.meta.env.VITE_BUILD;

app.mixin(GlobalMixin);
app.mixin(GlobalApiMixin);

router.app = app;
app.use(router);

app.use(Quasar, {
  plugins: {
    LocalStorage,
    SessionStorage,
    Dialog,
    Notify
  }, 
  lang: {}, 
  config: store.config, 
  // extras: ['material-icons-outlined']
});

// Tell app to use the I18n instance
app.use(i18n);
app.component('Header', Header);
app.component('HelpButton', HelpButton);
app.component('CustomDialog', CustomDialog);

store.app = app;
store.q = app.config.globalProperties.$q;

let keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL, 
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENTID
});

function saveTokens() {
  app.config.globalProperties.$q.localStorage.setItem('accessToken', keycloak.token);
  // localStorage.setItem('user-token', keycloak.token);
  app.config.globalProperties.$q.localStorage.setItem('refreshToken', keycloak.refreshToken);
  app.config.globalProperties.$q.localStorage.setItem('tokenExpiry', Date.now() + keycloak.tokenParsed.exp * 1000);
}

function getStoredTokens() {
  return {
    accessToken: app.config.globalProperties.$q.localStorage.getItem('accessToken'),
    // accessToken: localStorage.getItem('user-token'),
    refreshToken: app.config.globalProperties.$q.localStorage.getItem('refreshToken'),
    tokenExpiry: app.config.globalProperties.$q.localStorage.getItem('tokenExpiry')
  };
}

function startTokenRefresh() {
  setInterval(() => {
    if (navigator.onLine) {
      keycloak.updateToken(60).then(refreshed => {
        if (refreshed) {
          saveTokens();
          console.log('Token refreshed');
        }
      }).catch(() => {
        console.log('Failed to refresh token, logging in');
        keycloak.login();
      });
    }
  }, 60000);
}

let isKeycloakInitialized = false;
let isLoggingOut = false;

keycloak.onAuthError = error => {
  console.error('Keycloak auth error:', error)
}

async function initKeycloak() {
  console.log("Initializing Keycloak");

  try {

    const authenticated = await keycloak.init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: false
    });

    app.config.globalProperties.$keycloak = keycloak;
    startTokenRefresh();
  } catch (err) {
    console.error("Keycloak init failed", err);
  }
   
  app.config.globalProperties.$keycloak = keycloak;
  app.mount("#app");
  
}

store.isOnline = await checkServerOnline();


await initKeycloak();



// forced software updates check
// function checkForSWUpdates() {
//   if ('serviceWorker' in navigator) {
//     navigator.serviceWorker.getRegistration().then(registration => {
//       if (registration) {
//         registration.update();
//       }
//     });
//   }
// }
// setInterval(checkForSWUpdates, 60000); // check every 1min


//async function checkOnlineStatusSync() {
  // const xhr = new XMLHttpRequest();
  // xhr.open('GET', import.meta.env.VITE_ROOT_API + 'CommonAnon/Ping', false); // false za synchronous
  // try {
  //   xhr.send();
  //   console.log('Checking online status', xhr.status);
  //   return xhr.status >= 200 && xhr.status < 300;
  // } catch (error) {
  //   console.log('Checking online status', error);
  //   return false;
  // }
//}

async function checkServerOnline() {
  try {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 3000); // timeout safety
    const res = await fetch(
      import.meta.env.VITE_ROOT_API + 'CommonAnon/Ping',
      { signal: controller.signal }
    );
    return res.ok;
  } catch {
    return false;
  }
}

// window.addEventListener('online', async () => {
//   console.log(`Main: online ${navigator.onLine}, isKeycloakInitialized: ${isKeycloakInitialized}`);
//   if(!isKeycloakInitialized){
//     initKeycloak();
//   }
//     store.isOnline = await checkServerOnline();
// });

// window.addEventListener('offline', () => {
//   console.log(`Main: offline ${navigator.onLine}`);
//   store.isOnline = false;
// });

//frame-src 'self'; frame-ancestors 'self' http://localhost:8080 http://161.53.18.28:8080 https://app.ai4soilhealth.eu; object-src 'none';